import moment from 'moment';

import { StorageManager } from '../../../../../../common/storage-manager.class';
import { environment } from '../../../../../../../environments/environment';
import {
    DropdownData,
    FilteredValue,
    InputType,
    Value,
    Form,
    FormField,
    FormFieldConditions,
    FormSection,
    FormType,
} from '@fitogest/reactive-form-builder/interfaces';
import { TareaSiembraModel } from '../../../../../../models/tareas/tarea-siembra.model';
import { ClienteModel } from '../../../../../../models/cliente.model';
import { FincasModel } from '../../../../../../models/form-common/fincas.module';
import { SectorModel } from '../../../../../../models/form-common/sector.model';
import { ParcelasModel } from '../../../../../../models/form-common/parcelas.model';
import { MantenimientoModel } from '../../../../../../models/trabajadores/mantenimiento.model';
import { VisitasVariedadMesModel } from '../../../../../../models/visitas_variedad_mes/visitas_variedad_mes.model';
import { ProductosModel } from '../../../../../../models/productos/productos.model';
import { TratamientosModel } from '../../../../../../models/tratamientos/tratamientos.model';
import { MaquinariaModel } from '../../../../../../models/trabajadores/maquinaria.model';
import { getDropdownSelected, getDropdownSelectedSingle, list } from '@fitogest/reactive-form-builder/utils';

export class SiembraFormDefinition {
    public readonly appName = environment.appName;
    public readonly applicationType = environment.features.applicationType;
    public readonly horticola = environment.features.horticola;
    public readonly isViverogest = this.applicationType === 'viverogest' && !this.horticola;
    public readonly fincaName = environment.features.fincaName;
    public readonly sectorName = environment.features.sectorName;
    public readonly parcelasName = environment.features.parcelasName;
    public readonly clienteName = environment.features.clienteName;
    public readonly textoSuperficie = environment.features.textoSuperficie;
    public readonly tipoSuperficieParcelas = environment.features.hasSuperficieCultivada ? 'S. C.' : 'S. Sigpac';
    public readonly hasClients = environment.features.hasClients;
    public readonly canSeeClientes = environment.features.hasClients &&
        environment.features.userCanSeeAllRecords &&
        environment.features.userCanSeeClientes
        ? true
        : StorageManager.getUser().rol === 'admin';
    public readonly showTareasSiembraProductos = environment.features.showTareasSiembraProductos;
    public readonly showTareasSiembraMaquinarias = environment.features.showTareasSiembraMaquinarias;

    public formType!: FormType;
    public model: Partial<TareaSiembraModel> = {};
    public formFields!: Form<TareaSiembraModel>;

    public clientes: DropdownData<ClienteModel> = list<ClienteModel>(['...', null]);
    public fincas: DropdownData<FincasModel> = list<FincasModel>(['...', null]);
    public sectores: DropdownData<SectorModel> = list<SectorModel>(['...', null]);
    public parcelas: DropdownData<ParcelasModel> = list<ParcelasModel>(['...', null]);
    public trabajadores: DropdownData<MantenimientoModel> = list<MantenimientoModel>(['...', null]);
    public cultivos: DropdownData<VisitasVariedadMesModel> = list<VisitasVariedadMesModel>(['...', null]);
    public variedades: DropdownData<VisitasVariedadMesModel> = list<VisitasVariedadMesModel>(['...', null]);
    public cultivosPrecedentes: DropdownData<VisitasVariedadMesModel> = list<VisitasVariedadMesModel>(['...', null]);
    public productos: DropdownData<ProductosModel> = list<ProductosModel>(['...', null]);
    public tratamientos: DropdownData<TratamientosModel> = list<TratamientosModel>(['...', null]);
    public maquinarias: DropdownData<MaquinariaModel> = list<MaquinariaModel>(['...', null]);

    public tipoRiego: DropdownData<string> = list<string>('...', 'Cintas', 'Gotero', 'Aspersión');
    public productoArmentia: DropdownData<string> = list<string>('...', 'Systiva', 'Raxil Plus');
    public tipoSemilla: DropdownData<string> = list<string>(['...', null], 'R1', 'R2', 'S');
    public tipoSiembra: DropdownData<string> = list<string>('...', 'Seco', 'Agua');
    public tipoLabor: DropdownData<string> = this.appName !== 'cartagenafresh'
        ? list<string>(['...', null], 'CHISEL', 'GRADA', 'LABRAR', 'SEMBRAR', 'RECOLECTAR')
        : list<string>(['SEMBRAR', 'SEMBRAR']);
    public semilla: DropdownData<string> = list<string>(['...', null], ['R - Reempleo', 'R'], ['C - Certificada', 'C'], ['N - Nada', 'N']);
    public sustrato: DropdownData<string> = list<string>('...', 'BVB', 'BRILL', 'COCO');
    public camara: DropdownData<string> = list<string>('...', 'Calor', 'Frio', '4º', '30º', 'Descarga Directa (no pasa por cámara)');
    public rpmValues: DropdownData<string> = list<string>(['...', null], '1000', '1100', '1200', '1300', '1400',
        '1500', '1600', '1700', '1800', '1900', '2000');

    public fieldNames: FormField<TareaSiembraModel>[] = [];
    public fieldLabels: string[] = [];

    private readonly client = StorageManager.getClient();
    private readonly user = StorageManager.getUser();
    private selectedSectorIdsCacheKey = '';
    private selectedSectorIdsCacheValue = new Set<string>();
    private selectedCultivoCodigoCacheKey = '';
    private selectedCultivoCodigoCacheValue = '';

    constructor(type: FormType, model: Partial<TareaSiembraModel>) {
        this.formType = +type;
        this.model = this.normalizePreviousData(model as TareaSiembraModel);
        this.ensureModelDefaults();

        this.formFields = {
            name: 'tareas-siembra',
            header: this.getHeader(),
            type: this.formType,
            sections: this.getFormSections(),
            table: null,
            uploadButton: {
                isEnabled: false,
                label: '',
            },
            signed: false,
            previousDataTransform: (previousData: TareaSiembraModel): TareaSiembraModel => this.normalizePreviousData(previousData),
        };
    }

    public calculateHorasTotales(): string {
        return this.formatHoursMinutes(
            this.normalizeTimeValue(this.model.hora_inicio),
            this.normalizeTimeValue(this.model.hora_fin),
        );
    }

    public updateCultivoVariedadVisibility(): void {
        const variedadField = this.getFieldByName('id_variedad');

        if (!variedadField) {
            return;
        }

        variedadField.input.dropdownConfiguration = {
            ...(variedadField.input.dropdownConfiguration ?? {}),
            valuePrimaryKey: 'id',
            filter: (it): boolean => this.filterVariedad(it),
        };
    }

    private getHeader(): string {
        if (this.formType === FormType.EDITION) {
            return 'Editar ' + (this.horticola ? 'Cultivo' : 'Tarea Siembra');
        }

        if (this.formType === FormType.DUPLICATION) {
            return 'Duplicar ' + (this.horticola ? 'Cultivo' : 'Tarea Siembra');
        }

        return 'Crear ' + (this.horticola ? 'Cultivo' : 'Tarea Siembra');
    }

    private getFormSections(): FormSection<TareaSiembraModel>[] {
        const sections: FormSection<TareaSiembraModel>[] = [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.horticola ? this.getHorticolaFields() : this.isViverogest ? this.getViverogestFields() : this.getBaseFields(),
                order: 1,
            },
            {
                name: 'Datos de la maquinaria',
                width: 6,
                isVisible: this.showTareasSiembraMaquinarias && !this.horticola,
                fields: this.getMaquinariaFields(this.showTareasSiembraMaquinarias && !this.horticola),
                order: 2,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getHiddenFields(),
                order: 3,
            },
        ];

        return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getBaseFields(): FormField<TareaSiembraModel>[] {
        return this.pushFields([
            this.getClientField(1),
            {
                name: 'check_previa',
                label: 'NO < Tarea previa a la siembra > SÍ',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 2,
            },
            {
                name: 'fecha_inicio',
                label: 'Fecha de inicio',
                input: { type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'cartagenafresh' }),
                order: 3,
            },
            {
                name: 'fecha_fin',
                label: this.appName === 'cartagenafresh' ? 'Fecha Plantación' : 'Fecha de fin',
                input: {
                    type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.CALENDAR,
                    isEditable: true,
                    ...(this.getDefaultFechaFin() !== undefined ? { defaultValue: this.getDefaultFechaFin() } : {}),
                },
                width: 3,
                conditions: this.conditions(),
                order: 4,
            },
            {
                name: 'hora_inicio',
                label: 'Hora Inicio',
                input: { type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.TIME, isEditable: true },
                width: 3,
                conditions: this.conditions({
                    isRequired: environment.appName === 'agrotata',
                    isVisible: environment.features.timeStartStopTareas && this.appName !== 'hernandorena',
                }),
                order: 5,
            },
            {
                name: 'hora_fin',
                label: 'Hora Fin',
                input: {
                    type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.TIME,
                    isEditable: true,
                    ...(this.getDefaultHoraFin() !== undefined ? { defaultValue: this.getDefaultHoraFin() } : {}),
                },
                width: 3,
                conditions: this.conditions({
                    isRequired: environment.appName === 'agrotata',
                    isVisible: environment.features.timeStartStopTareas && this.appName !== 'hernandorena',
                }),
                order: 6,
            },
            {
                name: 'horas_totales',
                label: 'Horas',
                input: { type: InputType.ONLY_TEXT, isEditable: true },
                width: 3,
                computed: {
                    dependsOn: ['hora_inicio', 'hora_fin'],
                    resolve: (context) => this.formatHoursMinutes(
                        this.normalizeTimeValue(context.getValue('hora_inicio')),
                        this.normalizeTimeValue(context.getValue('hora_fin')),
                    ),
                },
                updateValue: () => this.calculateHorasTotales(),
                conditions: this.conditions({ isVisible: environment.features.showTareasMto || environment.features.tareasHorasTotales }),
                order: 7,
            },
            {
                name: 'facturado',
                label: 'Facturado',
                input: { type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.SWITCH, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName === 'agrosalvi' }),
                order: 8,
            },
            {
                name: 'factura_albaran',
                label: 'Factura/Albarán',
                input: { type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName === 'agrosalvi' }),
                order: 9,
            },
            this.getFincaField(10),
            this.getSectorField(11, true),
            this.getParcelasField(12, true),
            {
                name: this.isReadonlyMode() ? 'cultivo_precedente' : 'id_cultivo_precedente',
                dependencies: [
                    {
                        source: 'id_finca',
                        target: this.isReadonlyMode() ? 'cultivo_precedente' : 'id_cultivo_precedente',
                        kind: 'filter'
                    },
                    {
                        source: 'id_sector',
                        target: this.isReadonlyMode() ? 'cultivo_precedente' : 'id_cultivo_precedente',
                        kind: 'filter'
                    },
                    {
                        source: 'ids_parcelas',
                        target: this.isReadonlyMode() ? 'cultivo_precedente' : 'id_cultivo_precedente',
                        kind: 'filter'
                    }
                ],
                label: 'Cultivo precedente',
                input: this.isReadonlyMode()
                    ? { type: InputType.ONLY_TEXT, isEditable: true }
                    : {
                        type: InputType.DROPDOWN_SEARCH,
                        data: this.cultivosPrecedentes,
                        isEditable: true,
                        dropdownConfiguration: {
                            valuePrimaryKey: 'id',
                            filter: (it): boolean => this.filterDistinctCultivo(it),
                        },
                    },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'cartagenafresh' }),
                order: 13,
            },
            {
                name: 'tipo_tratamiento',
                label: 'Tipo de tratamiento',
                input: {
                    type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.DROPDOWN,
                    data: list<string>(['...', null], 'Semillas', 'Plantones', 'Cosecha'),
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName === 'pyf' }),
                order: 14,
            },
            {
                name: 'tipo_riego',
                label: 'Tipo riego',
                input: {
                    type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.DROPDOWN,
                    data: this.tipoRiego,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName === 'cartagenafresh' }),
                order: 15,
            },
            {
                name: 'motivo',
                label: 'Motivo',
                input: { type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName === 'pyf' }),
                order: 16,
            },
            this.getProductoField(17),
            {
                name: 'dosis_siembra',
                label: this.applicationType === 'cropgest' ? 'Dosis Kg' : 'Dosis Kg/Ha',
                input: { type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 18,
            },
            {
                name: 'tipo_labor',
                label: 'Tipo de labor',
                input: {
                    type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.DROPDOWN,
                    data: this.tipoLabor,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions(),
                order: 19,
            },
            {
                name: 'semilla',
                label: 'Semilla',
                input: {
                    type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.DROPDOWN,
                    data: this.semilla,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions(),
                order: 20,
            },
            {
                name: 'tipo_semilla',
                label: (this.appName === 'sequiador' || this.appName === 'SIEX') ? 'Tipo de siembra' : 'Tipo de semilla',
                input: {
                    type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.DROPDOWN,
                    data: (this.appName === 'sequiador' || this.appName === 'SIEX') ? this.tipoSiembra : this.tipoSemilla,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'cartagenafresh' }),
                order: 21,
            },
            {
                name: 'lote_semilla',
                label: 'Lote Semilla',
                input: { type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName === 'cartagenafresh' }),
                order: 22,
            },
            this.getObservacionesField(23, 6),
        ]);
    }

    private getViverogestFields(): FormField<TareaSiembraModel>[] {
        return this.pushFields([
            {
                name: this.isReadonlyMode() ? 'trabajadores_nombre' : 'ids_trabajadores',
                label: 'Personal Siembra',
                input: this.isReadonlyMode()
                    ? { type: InputType.ONLY_TEXT, isEditable: true }
                    : {
                        type: InputType.MULTISELECT,
                        data: this.trabajadores,
                        isEditable: true,
                        dropdownConfiguration: {
                            valuePrimaryKey: 'id',
                            filter: (it): boolean => (it.value as MantenimientoModel | null)?.activo?.toString() === '1',
                        },
                    },
                width: 4,
                conditions: this.conditions(),
                order: 1,
            },
            this.getFincaField(2),
            this.getSectorField(3, true),
            this.getParcelasField(4, true),
            this.getObservacionesField(18, 4),
        ]);
    }

    private getHorticolaFields(): FormField<TareaSiembraModel>[] {
        return this.pushFields([
            {
                name: 'fecha_inicio',
                label: 'Fecha de inicio',
                input: { type: this.dateInputType(), isEditable: true },
                width: 4,
                conditions: this.conditions({ isVisible: this.appName !== 'cartagenafresh' }),
                order: 1,
            },
            {
                name: 'fecha_fin',
                label: this.appName === 'cartagenafresh' ? 'Fecha Plantación' : 'Fecha de fin',
                input: {
                    type: this.dateInputType(),
                    isEditable: true,
                    ...(this.getDefaultFechaFin() !== undefined ? { defaultValue: this.getDefaultFechaFin() } : {}),
                },
                width: 4,
                conditions: this.conditions(),
                order: 2,
            },
            this.getFincaField(3),
            this.getSectorField(4, false),
            this.getParcelasField(5, false),
            {
                name: this.isReadonlyMode() ? 'cultivo' : 'id_cultivo',
                label: 'Cultivo',
                input: this.isReadonlyMode()
                    ? { type: InputType.ONLY_TEXT, isEditable: true }
                    : {
                        type: InputType.DROPDOWN_SEARCH,
                        data: this.cultivos,
                        isEditable: true,
                        dropdownConfiguration: { valuePrimaryKey: 'id' },
                    },
                width: 4,
                conditions: this.conditions(),
                order: 6,
            },
            {
                name: this.isReadonlyMode() ? 'variedad' : 'id_variedad',
                dependencies: [
                    {
                        source: 'id_cultivo',
                        target: this.isReadonlyMode() ? 'variedad' : 'id_variedad',
                        kind: 'filter'
                    }
                ],
                label: 'Variedad',
                input: this.isReadonlyMode()
                    ? { type: InputType.ONLY_TEXT, isEditable: true }
                    : {
                        type: InputType.DROPDOWN_SEARCH,
                        data: this.variedades,
                        isEditable: true,
                        dropdownConfiguration: {
                            valuePrimaryKey: 'id',
                            filter: (it): boolean => this.filterVariedad(it),
                        },
                    },
                width: 4,
                conditions: this.conditions(),
                order: 7,
            },
        ]);
    }

    private getMaquinariaFields(isSectionVisible: boolean): FormField<TareaSiembraModel>[] {
        return this.pushFields([
            {
                name: this.appName !== 'bravosol' ? 'id_maquinaria' : 'ids_maquinaria',
                label: 'Maquinaria',
                input: {
                    type: this.isReadonlyMode()
                        ? InputType.ONLY_TEXT
                        : this.appName !== 'bravosol' ? InputType.DROPDOWN_SEARCH : InputType.MULTISELECT,
                    data: this.maquinarias,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it): boolean => (it.value as MaquinariaModel | null)?.activo?.toString() === '1',
                    },
                },
                width: 4,
                conditions: this.conditions({ isVisible: isSectionVisible && this.appName !== 'bravosol' }),
                order: 1,
            },
        ]);
    }

    private getHiddenFields(): FormField<TareaSiembraModel>[] {
        const hiddenFields: FormField<TareaSiembraModel>[] = [
            {
                name: 'id_usuario',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false, defaultValue: this.user.id ?? '' },
                width: 1,
                updateValue: () => String(this.model.id_usuario ?? this.user.id ?? ''),
                conditions: this.conditions(),
                order: 2,
            },
            {
                name: 'cultivo_precedente',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 1,
                updateValue: () => getDropdownSelectedSingle(this.cultivosPrecedentes)?.cultivo ?? this.model.cultivo_precedente ?? '',
                conditions: this.conditions({ isVisible: !this.horticola && !this.isReadonlyMode() }),
                order: 3,
            },
            {
                name: 'cultivo',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 1,
                updateValue: () => this.resolveCultivoValue(),
                conditions: this.conditions({ isVisible: !this.isReadonlyMode() }),
                order: 4,
            },
            {
                name: 'variedad',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 1,
                updateValue: () => this.resolveVariedadValue(),
                conditions: this.conditions({ isVisible: !this.isReadonlyMode() }),
                order: 5,
            },
            {
                name: 'sup_cultivada',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 1,
                updateValue: () => this.getSelectedParcelas().map((it) => it.superficie_cultivada).join(';'),
                conditions: this.conditions({ isVisible: !this.horticola && !this.isReadonlyMode() }),
                order: 6,
            },
        ];

        if (!(this.canSeeClientes && !this.client && this.hasClients) && !this.isReadonlyMode()) {
            hiddenFields.push({
                name: 'id_cliente',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false, defaultValue: this.resolveClientId() },
                width: 1,
                updateValue: () => this.resolveClientId(),
                conditions: this.conditions(),
                order: 7,
            });
        }

        return hiddenFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getClientField(order: number): FormField<TareaSiembraModel> {
        const visible = this.canSeeClientes && !this.client && this.hasClients;

        return {
            name: visible || this.isReadonlyMode() ? (this.isReadonlyMode() ? 'cliente_nombre' : 'id_cliente') : 'id_cliente_hidden_visual',
            label: this.clienteName,
            input: visible
                ? {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.clientes,
                    isEditable: true,
                    dropdownConfiguration: { valuePrimaryKey: 'id' },
                }
                : { type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.HIDDEN, isEditable: false },
            width: 3,
            conditions: this.conditions({ isRequired: visible, isVisible: visible || this.isReadonlyMode() }),
            order,
        };
    }

    private getFincaField(order: number): FormField<TareaSiembraModel> {
        const readonly = this.isReadonlyMode();

        return {
            name: readonly ? 'finca_nombre' : 'id_finca',
            dependencies: [
                {
                    source: 'id_cliente',
                    target: readonly ? 'finca_nombre' : 'id_finca',
                    kind: 'filter'
                }
            ],
            label: this.fincaName,
            input: readonly
                ? { type: InputType.ONLY_TEXT, isEditable: true }
                : {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.fincas,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it): boolean => this.filterFinca(it),
                    },
                },
            width: 3,
            conditions: this.conditions(),
            order,
        };
    }

    private getSectorField(order: number, multi: boolean): FormField<TareaSiembraModel> {
        const readonly = this.isReadonlyMode();

        return {
            name: readonly ? 'sector_nombre' : 'id_sector',
            dependencies: [
                {
                    source: 'id_finca',
                    target: readonly ? 'sector_nombre' : 'id_sector',
                    kind: 'filter'
                }
            ],
            label: this.sectorName,
            input: readonly
                ? { type: InputType.ONLY_TEXT, isEditable: true }
                : {
                    type: multi ? InputType.MULTISELECT_SEARCH : InputType.DROPDOWN_SEARCH,
                    data: this.sectores,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it): boolean => this.filterSector(it),
                    },
                },
            width: 3,
            conditions: this.conditions(),
            order,
        };
    }

    private getParcelasField(order: number, multi: boolean): FormField<TareaSiembraModel> {
        const readonly = this.isReadonlyMode();

        return {
            name: readonly ? 'parcelas_nombres' : 'ids_parcelas',
            dependencies: [
                {
                    source: 'id_finca',
                    target: readonly ? 'parcelas_nombres' : 'ids_parcelas',
                    kind: 'filter'
                },
                {
                    source: 'id_sector',
                    target: readonly ? 'parcelas_nombres' : 'ids_parcelas',
                    kind: 'filter'
                }
            ],
            label: this.parcelasName,
            input: readonly
                ? { type: InputType.ONLY_TEXT, isEditable: true }
                : {
                    type: multi ? InputType.MULTISELECT_SEARCH : InputType.DROPDOWN_SEARCH,
                    data: this.parcelas,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        ...(multi ? {
                            summation: {
                                summand: 'superficie_cultivada',
                                name: this.tipoSuperficieParcelas,
                                uds: this.textoSuperficie,
                                format: (total: number): string => total.toLocaleString('es-ES', {
                                    useGrouping: true,
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                }),
                            },
                        } : {}),
                        filter: (it): boolean => this.filterParcela(it),
                    },
                },
            width: 3,
            conditions: this.conditions({ isRequired: !readonly }),
            order,
        };
    }

    private getProductoField(order: number): FormField<TareaSiembraModel> {
        const readonly = this.isReadonlyMode();
        const useArmentiaList = this.appName === 'armentia';

        if (readonly) {
            return {
                name: this.showTareasSiembraProductos ? 'producto_nombre' : 'id_producto',
                label: 'Producto/Tratamiento',
                input: { type: InputType.ONLY_TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.showTareasSiembraProductos || this.appName !== 'cartagenafresh' }),
                order,
            };
        }

        return {
            name: 'id_producto',
            label: 'Producto/Tratamiento',
            input: {
                type: InputType.DROPDOWN_SEARCH,
                data: useArmentiaList ? this.productoArmentia : this.productos,
                isEditable: true,
                ...(useArmentiaList ? {} : {
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it): boolean => (it.value as ProductosModel | null)?.tipo === 'fitosanitario',
                    },
                }),
            },
            width: 3,
            conditions: this.conditions({ isVisible: this.showTareasSiembraProductos }),
            order,
        };
    }

    private getObservacionesField(order: number, width: number): FormField<TareaSiembraModel> {
        return {
            name: 'observaciones',
            label: 'Observaciones',
            input: {
                type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.TEXTAREA,
                isEditable: true,
                textAreaConfiguration: {
                    hasVoiceTranscription: true,
                    canTakePhoto: false,
                    photoField: undefined,
                },
            },
            width,
            conditions: this.conditions(),
            order,
        };
    }

    private filterFinca(it: FilteredValue<unknown>): boolean {
        const finca = it.value as FincasModel | null;

        if (!finca) {
            return true;
        }

        if (!this.canSeeClientes || this.formType === FormType.DUPLICATION) {
            return true;
        }

        const selectedCliente = getDropdownSelectedSingle(this.clientes);

        if (selectedCliente) {
            return finca.id_cliente?.toString() === selectedCliente.id?.toString();
        }

        if (this.client) {
            return finca.id_cliente?.toString() === this.client.id?.toString();
        }

        return true;
    }

    private filterSector(it: FilteredValue<unknown>): boolean {
        const sector = it.value as SectorModel | null;

        if (!sector) {
            return true;
        }

        const selectedFincaId = this.getSelectedFincaId();

        return selectedFincaId !== '' && String(sector.id_finca ?? '') === selectedFincaId;
    }

    private filterParcela(it: FilteredValue<unknown>): boolean {
        const parcela = it.value as ParcelasModel | null;

        if (!parcela) {
            return true;
        }

        if (parcela.activo?.toString() !== '1') {
            return false;
        }

        const selectedSectorIds = this.getSelectedSectorIds();

        if (selectedSectorIds.size > 0) {
            return selectedSectorIds.has(String(parcela.id_sector ?? ''));
        }

        const selectedFincaId = this.getSelectedFincaId();

        return selectedFincaId !== '' && String(parcela.id_finca ?? '') === selectedFincaId;
    }

    private filterDistinctCultivo(it: FilteredValue<unknown>): boolean {
        const cultivo = it.value as VisitasVariedadMesModel | null;

        if (!cultivo) {
            return true;
        }

        const firstSameLabel = this.cultivosPrecedentes.values.find((value) => value.label === it.label);

        return firstSameLabel?.value?.id === cultivo.id;
    }

    private filterVariedad(it: FilteredValue<unknown>): boolean {
        const variedad = it.value as VisitasVariedadMesModel | null;

        if (!variedad) {
            return true;
        }

        const selectedCultivoCodigo = this.getSelectedCultivoCodigo();

        if (selectedCultivoCodigo === '') {
            return true;
        }

        return String(variedad.codigo_cultivo ?? '') === selectedCultivoCodigo;
    }


    private getSelectedFincaId(): string {
        const selectedFinca = getDropdownSelectedSingle(this.fincas);
        const selectedFincaId = this.getDropdownComparableId(this.fincas, selectedFinca);

        if (selectedFincaId !== '') {
            return selectedFincaId;
        }

        return this.getDropdownComparableId(this.fincas, this.model.id_finca);
    }

    private getSelectedSectorIds(): Set<string> {
        const selectedSectors = getDropdownSelected(this.sectores);
        const cacheKey = [
            this.getDropdownSelectionCacheKey(this.sectores, selectedSectors),
            this.getDropdownComparableId(this.sectores, this.model.id_sector),
        ].join('|');

        if (cacheKey === this.selectedSectorIdsCacheKey) {
            return this.selectedSectorIdsCacheValue;
        }

        const selectedSectorIds = new Set<string>();
        const selectedValues = Array.isArray(selectedSectors) ? selectedSectors : [selectedSectors];

        for (const selectedSector of selectedValues) {
            const selectedSectorId = this.getDropdownComparableId(this.sectores, selectedSector);

            if (selectedSectorId !== '') {
                selectedSectorIds.add(selectedSectorId);
            }
        }

        if (selectedSectorIds.size === 0) {
            const modelSectorId = this.getDropdownComparableId(this.sectores, this.model.id_sector);

            if (modelSectorId !== '') {
                selectedSectorIds.add(modelSectorId);
            }
        }

        this.selectedSectorIdsCacheKey = cacheKey;
        this.selectedSectorIdsCacheValue = selectedSectorIds;

        return selectedSectorIds;
    }

    private getSelectedCultivoCodigo(): string {
        const selectedCultivo = getDropdownSelectedSingle(this.cultivos);
        const selectedCultivoCodigo = String(selectedCultivo?.codigo ?? '').trim();

        if (selectedCultivoCodigo !== '') {
            return selectedCultivoCodigo;
        }

        const modelCultivoId = this.getComparableId(this.model.id_cultivo);

        if (modelCultivoId === '') {
            return '';
        }

        const cacheKey = [modelCultivoId, this.cultivos.values.length, this.cultivos.filtered.length].join('|');

        if (cacheKey === this.selectedCultivoCodigoCacheKey) {
            return this.selectedCultivoCodigoCacheValue;
        }

        const cultivoOptions = this.cultivos.filtered.length > 0 ? this.cultivos.filtered : this.cultivos.values;
        const selectedOption = cultivoOptions.find((cultivo) => this.getComparableId(cultivo.value) === modelCultivoId);
        const selectedCodigo = String(selectedOption?.value?.codigo ?? '').trim();

        this.selectedCultivoCodigoCacheKey = cacheKey;
        this.selectedCultivoCodigoCacheValue = selectedCodigo;

        return selectedCodigo;
    }

    private getDropdownSelectionCacheKey<T>(dropdown: DropdownData<T>, selection: unknown): string {
        if (Array.isArray(selection)) {
            return selection.map((item) => this.getDropdownComparableId(dropdown, item)).join(';');
        }

        return this.getDropdownComparableId(dropdown, selection);
    }

    private getDropdownComparableId<T>(dropdown: DropdownData<T>, value: unknown): string {
        const directId = this.getComparableId(value);

        if (directId === '') {
            return '';
        }

        const directOption = dropdown.values.find((option) => this.dropdownOptionMatchesId(option, directId));

        if (directOption) {
            return this.getComparableId(directOption.value);
        }

        const normalizedLabel = directId.toLowerCase();
        const labelOption = dropdown.values.find((option) => this.dropdownOptionMatchesLabel(option, normalizedLabel));

        return this.getComparableId(labelOption?.value);
    }

    private dropdownOptionMatchesId<T>(option: Value<T>, id: string): boolean {
        if (this.getComparableId(option.value) === id) {
            return true;
        }

        return (option.valueAliases ?? [])
            .some((alias) => this.getComparableId(alias) === id);
    }

    private dropdownOptionMatchesLabel<T>(option: Value<T>, normalizedLabel: string): boolean {
        const optionLabels = [
            option.label,
            this.readRecordString(option.value, 'nombre'),
            this.readRecordString(option.raw, 'nombre'),
        ];

        return optionLabels
            .some((label) => label.trim().toLowerCase() === normalizedLabel);
    }

    private readRecordString(source: unknown, key: string): string {
        if (!source || typeof source !== 'object') {
            return '';
        }

        const value = (source as Record<string, unknown>)[key];

        return value === null || value === undefined ? '' : String(value);
    }

    private getComparableId(value: unknown): string {
        if (value === null || value === undefined || value === '') {
            return '';
        }

        if (typeof value === 'object') {
            const record = value as Record<string, unknown>;
            const id = record['id'];

            return id === null || id === undefined || id === '' ? '' : String(id);
        }

        return String(value);
    }

    private resolveCultivoValue(): string {
        if (this.horticola) {
            return getDropdownSelectedSingle(this.cultivos)?.cultivo ?? this.model.cultivo ?? '';
        }

        return this.getSelectedParcelas().map((it) => it.cultivo).join(';');
    }

    private resolveVariedadValue(): string {
        if (this.horticola) {
            return getDropdownSelectedSingle(this.variedades)?.variedad ?? this.model.variedad ?? '';
        }

        return this.getSelectedParcelas().map((it) => it.variedad).join(';');
    }

    private getSelectedParcelas(): ParcelasModel[] {
        const selected = getDropdownSelected(this.parcelas);

        if (Array.isArray(selected)) {
            return selected.filter((it): it is ParcelasModel => Boolean(it));
        }

        return selected ? [selected] : [];
    }


    private resolveClientId(): string {
        const modelClient = this.model.id_cliente;

        if (this.client?.id) {
            return String(this.client.id);
        }

        const selectedFinca = getDropdownSelectedSingle(this.fincas);

        if (selectedFinca?.id_cliente) {
            return String(selectedFinca.id_cliente);
        }

        if (typeof modelClient === 'object' && modelClient !== null) {
            return modelClient.id ?? '';
        }

        return String(modelClient ?? '');
    }

    private getDefaultFechaFin(): string | Date | undefined {
        if (!environment.features.horaFechaFinActualPorDefecto || this.formType === FormType.EDITION) {
            return undefined;
        }

        if (this.appName === 'sirga') {
            return undefined;
        }

        return new Date();
    }

    private getDefaultHoraFin(): string | undefined {
        if (!environment.features.horaFechaFinActualPorDefecto || this.formType === FormType.EDITION) {
            return undefined;
        }

        const now = new Date();

        return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    }

    private formatHoursMinutes(startTime: string, endTime: string): string {
        if (!startTime || !endTime) {
            return '';
        }

        const start = moment(startTime, 'HH:mm', true);
        const end = moment(endTime, 'HH:mm', true);

        if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
            return '';
        }

        const duration = moment.duration(end.diff(start));
        const hours = Math.floor(duration.asHours()).toString().padStart(2, '0');
        const minutes = duration.minutes().toString().padStart(2, '0');

        return `${hours}:${minutes}`;
    }

    private normalizeTimeValue(value: unknown): string {
        if (value instanceof Date) {
            return moment(value).format('HH:mm');
        }

        if (typeof value === 'string') {
            return value.trim();
        }

        return '';
    }


    private dateInputType(): InputType {
        return this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.CALENDAR;
    }

    private conditions(overrides: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true,
            ...overrides,
        };
    }

    private pushFields(fields: FormField<TareaSiembraModel>[]): FormField<TareaSiembraModel>[] {
        const visibleFields = fields.filter((field) => field.conditions.isVisible && field.input.type !== InputType.HIDDEN);

        this.fieldNames = this.fieldNames.concat(visibleFields);
        this.fieldLabels = this.fieldLabels.concat(visibleFields.map((field) => field.label));

        return fields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private isReadonlyMode(): boolean {
        return this.formType >= FormType.DELETION && this.formType !== FormType.GENERATION;
    }

    private getFieldByName(name: string): FormField<TareaSiembraModel> | null {
        for (const section of this.formFields.sections) {
            const found = section.fields.find((field) => field.name === name);

            if (found) {
                return found;
            }
        }

        return null;
    }

    private normalizePreviousData(previousData: TareaSiembraModel): TareaSiembraModel {
        const normalizedData: TareaSiembraModel = { ...previousData };

        normalizedData.id_sector = (this.horticola
            ? this.normalizeSingleValue(previousData.id_sector)
            : this.normalizeMultiValue(previousData.id_sector)) as Exclude<TareaSiembraModel['id_sector'], undefined>;
        normalizedData.ids_parcelas = (this.horticola
            ? this.normalizeSingleValue(previousData.ids_parcelas)
            : this.normalizeMultiValue(previousData.ids_parcelas)) as Exclude<TareaSiembraModel['ids_parcelas'], undefined>;

        return normalizedData;
    }

    private normalizeMultiValue(value: unknown): unknown[] {
        if (Array.isArray(value)) {
            return value.filter((item) => item !== null && item !== undefined && item !== '');
        }

        if (value === null || value === undefined || value === '') {
            return [];
        }

        return String(value)
            .split(';')
            .map((item) => item.trim())
            .filter((item) => item !== '');
    }

    private normalizeSingleValue(value: unknown): unknown {
        if (Array.isArray(value)) {
            return value[0] ?? '';
        }

        if (typeof value === 'string' && value.includes(';')) {
            return value.split(';')[0]?.trim() ?? '';
        }

        return value ?? '';
    }

    private ensureModelDefaults(): void {
        if (this.appName === 'agrosalvi' && typeof this.model.facturado === 'string') {
            this.model.facturado = this.model.facturado === '1';
        }

        if (this.appName === 'viverospereira' && typeof this.model.no_esta === 'string') {
            this.model.no_esta = this.model.no_esta === '1';
        }

        if (typeof this.model.check_previa === 'string') {
            this.model.check_previa = this.model.check_previa === '1' || this.model.check_previa === 'true';
        }
    }
}
